/** @jsxImportSource @emotion/react */
import {useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {createSelector} from "reselect";
import {useTranslation} from "react-i18next";
import {Icon, Spinner} from "@blueprintjs/core";

// Libs
import Button from "../common/components/button";
import {primaryColor} from "../common/libs/theme-lib";
import moment from "moment";

// Components
import RenderContent from "../common/components/render-content";
import ClientSelector from "../common/components/client-selector";
import SwitchDateFilters from "../common/components/switch-date-filters";

// Hooks
import {useAuth} from "../auth/auth";
import {useFilters} from "../filters/filters";
import {useUserClientsData} from "../user-clients-data/user-clients-data";

// Actions
import {readDeliveryNotes} from "./delivery-notes-actions";
import {downloadPdf, downloadExcel} from "../content/content-actions";
import {cleanInvoicesState} from "../invoices/invoices-actions";
import {cleanTechnicalsSheetsState} from "../technical-sheets/technicals-sheets-actions";

// Selectors
import {deliveryNotesSelector, deliveryNotesIsFetchingSelector, deliveryNotesErrorSelector} from "./delivery-notes-selectors";
import {contentIsFetchingSelector, contentIdSelector, contentTypeSelector} from "../content/content-selectors";

// Component Selector
const deliveryNotesPageSelector = createSelector(
    deliveryNotesSelector,
    deliveryNotesIsFetchingSelector,
    deliveryNotesErrorSelector,
    contentIsFetchingSelector,
    contentIdSelector,
    contentTypeSelector,
    (deliveryNotes, deliveryNotesIsFetching, deliveryNotesError, contentIsFetching, contentId, contentType) => {
        return {
            deliveryNotes: deliveryNotes?.toJS() || [],
            deliveryNotesIsFetching,
            deliveryNotesError,
            contentIsFetching,
            contentId,
            contentType
        }
    }
);

// Redux dispatch hook
const useReduxDispatch = () => {
    const dispatch = useDispatch();
    return {
        readDeliveryNotes: (filters) => dispatch(readDeliveryNotes(filters)),
        downloadPdf: (pdfId, pdfType, clientId) => dispatch(downloadPdf(pdfId, pdfType, clientId)),
        downloadExcel: (excelId, excelType, clientId) => dispatch(downloadExcel(excelId, excelType, clientId)),
        cleanInvoicesState: () => dispatch(cleanInvoicesState()),
        cleanTechnicalsSheetsState: () => dispatch(cleanTechnicalsSheetsState())
    }
};

const DeliveryNotesPage = (props) => {

    const auth = useAuth();

    // filters
    const {
        deliveryNotesFilters,
        setDeliveryNotesFilters,
        dateFilters,
        setDateFilters,
        removeInvoicesFilters,
        removeTechnicalSheetsFilters
    } = useFilters();

    // Client Selector
    const {selectedClient} = useAuth();
    const {userClientsData} = useUserClientsData();
    const selectedClientData = userClientsData.find(client => client._id === selectedClient);

    // Redux
    const {
        deliveryNotes,
        contentIsFetching, contentId, contentType
    } = useSelector(state => deliveryNotesPageSelector(state));
    const {readDeliveryNotes, downloadPdf, downloadExcel, cleanInvoicesState, cleanTechnicalsSheetsState} = useReduxDispatch();

    // State
    const [documentId, setDocumentId] = useState(deliveryNotesFilters?.documentId || "");
    const [fromDate, setFromDate] = useState(moment(dateFilters?.fromDate || null) || null);
    const [toDate, setToDate] = useState(moment(dateFilters?.toDate || null) || null);
    const [isManualRange, setIsManualRange] = useState(dateFilters?.isManualRange === true);
    const [predefinedRangeValue, setPredefinedRangeValue] = useState(dateFilters?.predefinedValue || "");

    //i18n
    const {t} = useTranslation("deliveryNotes");

    // state of showWelcomeMessage
    const [showWelcomeMessage, setShowWelcomeMessage] = useState(!deliveryNotesFilters);
    // state of date change
    const [isDateChanged, setIsDateChanged] = useState(false);

    return (
        <>
            <div style={{
                display: "flex",
                alignItems: "flex-end",
                paddingBottom: "15px"
            }}>
                <div style={{flex: 1}}>
                    <div style={{
                        color: primaryColor,
                        fontSize: 28,
                        fontWeight: "bold",
                        marginBottom: 10
                    }}>{t("title", { client: selectedClientData && selectedClientData.name } )}</div>
                    <div style={{fontWeight: 100, fontSize: 18}}>{t("description")}</div>
                </div>
                {auth.userType === "admin" && (
                    <ClientSelector />
                )}
                {/* <TextInput
                    label={t("filters.deliveryNotesNumber")}
                    style={{marginRight: 15, minWidth: 100, width: 160}}
                    value={documentId}
                    setValue={setDocumentId}
                /> */}
                <SwitchDateFilters
                    fromValue={fromDate}
                    setFromValue={setFromDate}
                    toValue={toDate}
                    setToValue={setToDate}
                    isManualDateRange={isManualRange}
                    setIsManualDateRange={setIsManualRange}
                    predefinedValue={predefinedRangeValue}
                    setPredefinedValue={setPredefinedRangeValue}
                    setDateChanged={setIsDateChanged}
                />
                <Button
                    text={t("common:actions.search")}
                    action={() => {
                        const filters = {documentId};
                        // if isDateChange clean state and filters localStorage of invoices and technical sheets
                        if (isDateChanged) {
                            cleanInvoicesState();
                            cleanTechnicalsSheetsState();
                            removeInvoicesFilters();
                            removeTechnicalSheetsFilters();
                        }
                        readDeliveryNotes({
                            ...filters,
                            client: selectedClient,
                            documentId,
                            fromDate,
                            toDate
                        });
                        setDateFilters({
                            fromDate,
                            toDate,
                            isManualRange: isManualRange,
                            predefinedValue: predefinedRangeValue
                        });
                        setDeliveryNotesFilters(filters);
                        setShowWelcomeMessage(false);
                    }}
                    intent="primary"
                />
            </div>
            <RenderContent
                nameCollection={t("nameCollection")}
                data={deliveryNotes}
                showWelcomeMessage={showWelcomeMessage}
                renderHeader={false}
                config={{
                    sortable: true,
                    showFooter: true,
                    globalFilter: ""
                }}
                columns={
                    [{
                        id: "date",
                        Header: t("deliveryNotesTableColumns.date"),
                        accessor: "date",
                        type: "date"
                    }, {
                        id: "delivery_id",
                        Header: t("deliveryNotesTableColumns.delivery_id"),
                        accessor: "delivery_id",
                        filter: true
                    }, {
                        id: "order_id",
                        Header: t("deliveryNotesTableColumns.order_id"),
                        accessor: "order_id",
                        filter: true
                    }, {
                        id: "invoice_id",
                        Header: t("deliveryNotesTableColumns.invoice_id"),
                        accessor: "invoice_id",
                        filter: true
                    }, {
                        id: "invoice_date",
                        Header: t("deliveryNotesTableColumns.invoice_date"),
                        accessor: "invoice_date",
                        type: "date"
                    }, {
                        id: "actions",
                        Header: t("deliveryNotesTableColumns.pdf_order_download"),
                        accessor: "delivery_id",
                        type: "function",
                        disableSortBy: true,
                        minWidth: 60,
                        style: {
                            display: "flex",
                            justifyContent: "flex-end"
                        },
                        nodeContent: (value) => {
                            return contentIsFetching && contentType === "delivery-note" && contentId === value ? (
                                <div>
                                    <Spinner
                                        size={20}
                                        intent={"danger"}
                                    />
                                </div>
                            ) : (
                                <div css={{
                                    cursor: "pointer",
                                    transition: "all 0.4s",
                                    "&:hover": {
                                        color: primaryColor
                                    }
                                }} onClick={() => downloadPdf(value, "delivery-note", selectedClient)}>
                                    <Icon icon="download" iconSize={20} />
                                </div>
                            );
                        }
                    }, {
                        id: "actions-packing-list",
                        Header: t("deliveryNotesTableColumns.pdf_packing_list_download"),
                        accessor: "delivery_id",
                        type: "function",
                        disableSortBy: true,
                        minWidth: 60,
                        style: {
                            display: "flex",
                            justifyContent: "flex-end"
                        },
                        nodeContent: (value) => {
                            return contentIsFetching && contentType === "packing-list" && contentId === value ? (
                                <div>
                                    <Spinner
                                        size={20}
                                        intent={"danger"}
                                    />
                                </div>
                            ) : (
                                <div css={{
                                    cursor: "pointer",
                                    transition: "all 0.4s",
                                    "&:hover": {
                                        color: primaryColor
                                    }
                                }} onClick={() => downloadExcel(value, "packing-list", selectedClient)}>
                                    <Icon icon="download" iconSize={20} />
                                </div>
                            );
                        }
                    }]
                }
            />
        </>
    );
};

export default DeliveryNotesPage;